import { TranslatableTraining } from '@shared/training-translations/training-translations.controller';
import { TrainingTranslationFields } from '@models/training-translation.model';

// Snapshot of a training's authoring-language (source) display fields, captured before any overlay
// so we can always fall back to it when switching languages.
export interface TrainingSourceContent {
  name: string;
  description: string;
  coverImage: string | null;
}

// Capture the source (authoring-language) title, description and cover before overlaying a
// translation. Call this once, right after the training is loaded.
export function captureTrainingSource(
  training: TranslatableTraining,
): TrainingSourceContent {
  return {
    name: training.name,
    description: training.description,
    coverImage: training.coverImage ?? null,
  };
}

// Overlay `language`'s translation onto the training's display fields so every downstream
// view reflects it; fields the translation leaves empty fall back to the source snapshot.
export function applyTrainingTranslation(
  training: TranslatableTraining,
  source: TrainingSourceContent,
  language: string,
): void {
  const translation =
    language && language !== training.language
      ? (training.trainingTranslations ?? []).find(
          record => record.language === language,
        )
      : undefined;

  training.name = translation?.fields?.name || source.name;
  training.description = translation?.fields?.description || source.description;
  training.coverImage = translation?.coverImage || source.coverImage;
}

// The translation `fields` map for `language`, or null for source / an untranslated language.
// Lets views resolve per-key content with per-key fallback, without mutating the source.
export function trainingTranslationFields(
  training: TranslatableTraining,
  language: string,
): TrainingTranslationFields | null {
  if (!language || language === training.language) {
    return null;
  }
  return (
    (training.trainingTranslations ?? []).find(
      record => record.language === language,
    )?.fields ?? null
  );
}
